import {
  clearBusinessTravellerTripRequestTravellerFormMappings,
  getBusinessTravellerTripRequestTravellerFormMappings,
} from 'businessTraveller/storage/businessTravellerStorage'
import { sanitiseTripRequestLoyaltyPrograms } from 'lib/traveller/travellerUtil'

/**
 * Checks if there is an accommodation trip request to restore.
 * This is used to determine if we should apply trip request details to traveller forms.
 */
export function hasAccommodationTripRequestToRestore(): boolean {
  const tripRequestState = getBusinessTravellerTripRequestTravellerFormMappings()
  return !!(tripRequestState?.travellerFormMappings && tripRequestState.travellerFormMappings.length > 0)
}

/**
 * Pre-fills traveller forms with details from the trip request (firstName, lastName, email, etc.)
 * so users don't have to re-enter them when restoring a cart from an approved trip request.
 */
export function applyAccommodationTripRequestDetailsToForms(
  travellerForms: Array<App.Checkout.TravellerForm>,
): Array<App.Checkout.TravellerForm> {
  const tripRequestState = getBusinessTravellerTripRequestTravellerFormMappings()

  if (!tripRequestState?.travellerFormMappings || tripRequestState.travellerFormMappings.length === 0) {
    return travellerForms
  }

  const travellerFormMappings = tripRequestState.travellerFormMappings

  // Clear after reading - one-time use
  clearBusinessTravellerTripRequestTravellerFormMappings()

  // Build lookup by employeeId or guestId
  const mappingsMap = new Map(
    travellerFormMappings
      .filter(m => m.employeeId || m.guestId)
      .map(m => [(m.employeeId ?? m.guestId)!, m]),
  )

  return travellerForms.map((form) => {
    const key = form.employeeId ?? form.guestId
    const mapping = key ? mappingsMap.get(key) : undefined
    return mapping ? applyMappingToForm(form, mapping) : form
  })
}

/**
 * Applies trip request traveller mapping to a traveller form
 */
function applyMappingToForm(
  form: App.Checkout.TravellerForm,
  mapping: App.BusinessTraveller.TripRequestTravellerFormMapping,
): App.Checkout.TravellerForm {
  const details = mapping.guestTravellerDetails

  return {
    ...form,
    data: {
      ...form.data,
      // Only override if trip request has the value
      ...(details?.firstName && { firstName: details.firstName }),
      ...(details?.lastName && { lastName: details.lastName }),
      ...(details?.email && { email: details.email }),
      ...(details?.phonePrefix && { phonePrefix: details.phonePrefix }),
      ...(details?.phone && { phone: details.phone }),
    },
  }
}

/**
 * Checks if there is a flight trip request to restore from Redux state.
 * The approval request must already be fetched by useBusinessTravellerApprovalRequestFetcher.
 */
export function hasFlightTripRequestToRestore(state: App.State): boolean {
  const approvalRequestId = state.checkout.cart.approvalRequestId
  if (!approvalRequestId) return false

  const approvalRequests = state.businessTraveller.approvalRequests
  if (approvalRequests?.status !== 'success') return false

  const approvalRequest = approvalRequests.request.find(r => r.id === approvalRequestId)
  return !!approvalRequest && approvalRequest.type === 'flight'
}

/**
 * Pre-fills traveller forms with details from a flight trip request.
 * Unlike the accommodation flow (which uses localStorage), the flight flow reads
 * directly from Redux state since the approval request is already fetched by
 * useBusinessTravellerApprovalRequestFetcher in the flight checkout flow.
 *
 * Employee travellers are matched by employeeId (stable and consistent across
 * the search page and checkout). Guest travellers are matched positionally —
 * we simply assign the Nth guest's details to the Nth guest form. This avoids
 * the complexity of synchronising guestIds between the trip request, the flight
 * search page (which generates new UUIDs), localStorage, and checkout. The guest
 * count is guaranteed to be the same since it comes from the approval request,
 * so positional assignment is safe.
 */
export function applyFlightTripRequestDetailsToForms(
  travellerForms: Array<App.Checkout.TravellerForm>,
  state: App.State,
  formSchema: App.Checkout.FormSchema,
): Array<App.Checkout.TravellerForm> {
  const approvalRequestId = state.checkout.cart.approvalRequestId
  if (!approvalRequestId) return travellerForms

  const approvalRequests = state.businessTraveller.approvalRequests
  if (approvalRequests?.status !== 'success') return travellerForms

  const approvalRequest = approvalRequests.request.find(r => r.id === approvalRequestId)
  if (!approvalRequest || approvalRequest.type !== 'flight') return travellerForms

  const flightItems = approvalRequest.tripRequestItems.filter(
    (item): item is App.BusinessTraveller.FlightTripRequestItem => item.type === 'flight',
  )

  if (flightItems.length === 0) return travellerForms

  // Extract unique traveller details from trip request items.
  // Multiple flight items (outbound + return) may contain the same travellers, so deduplicate.
  const employeeDetailsMap = new Map<string, App.BusinessTraveller.GuestTravellerDetails>()
  const guestDetailsList: Array<App.BusinessTraveller.GuestTravellerDetails> = []
  const uniqueGuestIds = new Set<string>()

  for (const item of flightItems) {
    for (const traveller of item.tripRequestItemTravellers ?? []) {
      if (traveller.employeeId) {
        if (!employeeDetailsMap.has(traveller.employeeId)) {
          employeeDetailsMap.set(traveller.employeeId, traveller.guestTravellerDetails)
        }
      } else if (traveller.guestTravellerDetails.guestId) {
        const guestId = traveller.guestTravellerDetails.guestId
        if (guestId && !uniqueGuestIds.has(guestId)) {
          uniqueGuestIds.add(guestId)
          guestDetailsList.push(traveller.guestTravellerDetails)
        }
      }
    }
  }

  // Employee forms: match by employeeId.
  // Guest forms: assign positionally — the order of guest forms matches the order
  // they were added, and the count is the same as the trip request.
  let guestIndex = 0
  const travellerSchemas = Object.values(formSchema.properties)
  return travellerForms.map((form, index) => {
    const travellerSchema = travellerSchemas.length > index ? travellerSchemas[index] : undefined
    if (form.employeeId) {
      const details = employeeDetailsMap.get(form.employeeId)
      return details ? applyFlightMappingToForm(form, details, travellerSchema) : form
    }
    if (form.guestId && guestIndex < guestDetailsList.length) {
      const details = guestDetailsList[guestIndex]
      guestIndex++
      return applyFlightMappingToForm(form, details, travellerSchema)
    }
    return form
  })
}

function applyFlightMappingToForm(
  form: App.Checkout.TravellerForm,
  details: App.BusinessTraveller.GuestTravellerDetails,
  travellerSchema?: App.Checkout.TravellerFormSchema,
): App.Checkout.TravellerForm {
  return {
    ...form,
    data: {
      ...form.data,
      ...(details.firstName && { firstName: details.firstName }),
      ...(details.lastName && { lastName: details.lastName }),
      ...(details.middleName && { middleName: details.middleName }),
      ...(details.email && { email: details.email }),
      ...(details.phonePrefix && { phonePrefix: details.phonePrefix }),
      ...(details.phone && { phone: details.phone }),
      ...(details.dateOfBirth && { dateOfBirth: details.dateOfBirth }),
      ...(details.title && { title: details.title }),
      ...(details.postcode && { postcode: details.postcode }),
      ...(details.loyaltyPrograms && sanitiseTripRequestLoyaltyPrograms(details.loyaltyPrograms, travellerSchema)),
    },
  }
}
